import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AlertTriangle, X, TrendingDown, Target, Zap } from "lucide-react";

/**
 * Trade event notification system — loud, must-acknowledge modals for:
 *   - NEW TRADE detected (fresh AI setup recorded) — shows signal time + age
 *     so a late viewer can judge whether entering still makes sense.
 *   - Trade closes: SL hit / T1 / T2 / expired / INVALIDATED (trend flip).
 *
 * Polls /api/signals/recent-closes + /api/signals/recent-opens every 25s.
 * Acknowledgement persisted to localStorage so popups don't re-fire on refresh.
 */
const STORAGE_KEY = "tradeiq.ack_closes";        // JSON: { [ack_key]: ts }
const POLL_MS = 25_000;
const LOOKBACK_MIN = 90;                          // surface events within last 90 minutes
const FRESH_NEW_MIN = 15;                         // NEW-trade popups only when truly fresh
const FRESH_CLOSE_MIN = 45;                       // close popups (SL/T1) stay relevant longer

const STATUS_META = {
  miss:        { label: "STOP LOSS HIT",   tone: "danger",  icon: TrendingDown, action: "EXIT POSITION immediately." },
  hit_t1:      { label: "TARGET 1 HIT",    tone: "success", icon: Target,      action: "Book partial profit. SL moved to break-even." },
  hit_t2:      { label: "TARGET 2 HIT",    tone: "success", icon: Target,      action: "Full target reached. Close position." },
  expired:     { label: "TRADE EXPIRED",   tone: "neutral", icon: AlertTriangle, action: "Time budget elapsed without a hit. Exit if still open." },
  invalidated: { label: "TREND FLIPPED · TRADE INVALIDATED", tone: "neutral", icon: AlertTriangle, action: "AI bias flipped against this trade. EXIT if still open — a fresh setup in the opposite direction may follow." },
  new_trade:   { label: "NEW TRADE DETECTED", tone: "info",  icon: Zap,         action: "Fresh AI setup recorded. Check the signal time below — if you're late, price may have already moved from entry." },
};

function readAcks() {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}"); } catch { return {}; }
}
function writeAcks(acks) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(acks)); } catch { /* quota */ }
}

function ageMinutes(iso) {
  if (!iso) return null;
  const mins = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  return mins;
}

export default function TradeCloseModal() {
  const { user } = useAuth();
  const isPaid = user && (user.is_admin || user.plan === "pro");
  const [queue, setQueue] = useState([]);     // events waiting to be shown
  const [current, setCurrent] = useState(null);

  const poll = useCallback(async () => {
    if (!isPaid) return;
    try {
      const [closesRes, opensRes] = await Promise.all([
        api.get(`/signals/recent-closes?minutes=${LOOKBACK_MIN}`),
        api.get(`/signals/recent-opens?minutes=${LOOKBACK_MIN}`),
      ]);
      const acks = readAcks();
      const closes = (closesRes.data.closes || [])
        .filter((c) => c.signal_id && !acks[c.signal_id])
        .filter((c) => (ageMinutes(c.closed_at) ?? 0) <= FRESH_CLOSE_MIN)
        .map((c) => ({ ...c, kind: "close", ack_key: c.signal_id }));
      const opens = (opensRes.data.opens || [])
        .filter((c) => c.signal_id && !acks[`open_${c.signal_id}`])
        .filter((c) => (ageMinutes(c.generated_at) ?? 0) <= FRESH_NEW_MIN)
        .map((c) => ({ ...c, kind: "new", ack_key: `open_${c.signal_id}` }));
      // Anything older gets silently acked so it can never pile up into popup spam.
      const staleAcks = readAcks();
      let staleFound = false;
      (closesRes.data.closes || []).forEach((c) => {
        if (c.signal_id && !staleAcks[c.signal_id] && (ageMinutes(c.closed_at) ?? 0) > FRESH_CLOSE_MIN) { staleAcks[c.signal_id] = Date.now(); staleFound = true; }
      });
      (opensRes.data.opens || []).forEach((c) => {
        if (c.signal_id && !staleAcks[`open_${c.signal_id}`] && (ageMinutes(c.generated_at) ?? 0) > FRESH_NEW_MIN) { staleAcks[`open_${c.signal_id}`] = Date.now(); staleFound = true; }
      });
      if (staleFound) writeAcks(staleAcks);
      const unseen = [...closes, ...opens];
      if (unseen.length) setQueue((prev) => {
        const have = new Set(prev.map((c) => c.ack_key));
        return [...prev, ...unseen.filter((c) => !have.has(c.ack_key))];
      });
    } catch { /* silent */ }
  }, [isPaid]);

  useEffect(() => {
    if (!isPaid) return;
    poll();
    const id = setInterval(poll, POLL_MS);
    return () => clearInterval(id);
  }, [poll, isPaid]);

  // Pop the next item off the queue whenever modal is empty
  useEffect(() => {
    if (!current && queue.length > 0) {
      setCurrent(queue[0]);
      setQueue((q) => q.slice(1));
    }
  }, [current, queue]);

  const dismiss = () => {
    if (!current) return;
    const acks = readAcks();
    acks[current.ack_key] = Date.now();
    writeAcks(acks);
    setCurrent(null);
  };

  const dismissAll = () => {
    const acks = readAcks();
    if (current) acks[current.ack_key] = Date.now();
    queue.forEach((q) => { acks[q.ack_key] = Date.now(); });
    writeAcks(acks);
    setQueue([]);
    setCurrent(null);
  };

  if (!current) return null;

  const isNew = current.kind === "new";
  const meta = isNew ? STATUS_META.new_trade : (STATUS_META[current.status] || STATUS_META.expired);
  const Icon = meta.icon;
  const toneClasses = meta.tone === "danger"
    ? { ring: "border-[#EF4444]", bg: "bg-[#EF4444]/10", text: "text-[#EF4444]", pill: "bg-[#EF4444]" }
    : meta.tone === "success"
      ? { ring: "border-[#22C55E]", bg: "bg-[#22C55E]/10", text: "text-[#22C55E]", pill: "bg-[#22C55E]" }
      : meta.tone === "info"
        ? { ring: "border-[#007AFF]", bg: "bg-[#007AFF]/10", text: "text-[#3B9BFF]", pill: "bg-[#007AFF]" }
        : { ring: "border-[#F59E0B]", bg: "bg-[#F59E0B]/10", text: "text-[#F59E0B]", pill: "bg-[#F59E0B]" };

  const signalAge = isNew ? ageMinutes(current.generated_at) : null;
  const ageTone = signalAge == null ? "" : signalAge <= 15 ? "text-[#22C55E]" : signalAge <= 45 ? "text-[#F59E0B]" : "text-[#EF4444]";

  const grid = isNew
    ? [
        { l: "Entry", v: current.entry, cls: "text-white" },
        { l: "Stop Loss", v: current.stop_loss, cls: "text-[#EF4444]" },
        { l: "Target 1", v: current.target_1, cls: "text-[#22C55E]" },
        { l: "Target 2", v: current.target_2, cls: "text-[#22C55E]" },
      ]
    : [
        { l: "Entry", v: current.entry, cls: "text-white" },
        { l: "Closed at", v: current.closed_price, cls: "text-white" },
        { l: "Stop Loss", v: current.stop_loss, cls: "text-[#EF4444]" },
        { l: "Target 1", v: current.target_1, cls: "text-[#22C55E]" },
      ];

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
         data-testid="trade-close-modal" role="dialog" aria-modal="true">
      <div className={`relative max-w-md w-full bg-[#0a0a0a] border-2 ${toneClasses.ring} shadow-2xl`}>
        {/* Header strip */}
        <div className={`${toneClasses.pill} px-4 py-2 flex items-center gap-2 text-black`}>
          <Icon className="h-4 w-4" />
          <span className="font-heading font-bold uppercase tracking-widest text-sm">
            {meta.label}
          </span>
          <span className="ml-auto text-[10px] font-mono uppercase tracking-widest opacity-80">
            {current.mode === "swing" ? "swing" : "intraday"}
          </span>
        </div>

        {/* Body */}
        <div className="p-5 space-y-3">
          <div className="flex items-end justify-between">
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Symbol</div>
              <div className="font-heading text-2xl font-bold tracking-tight" data-testid="modal-symbol">
                {current.symbol}
                <span className="text-zinc-500 text-base font-normal ml-2 capitalize">· {current.trend}</span>
              </div>
            </div>
            {isNew && current.initial_probability != null && (
              <div className="text-right">
                <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Probability</div>
                <div className={`font-heading text-2xl font-black ${toneClasses.text}`} data-testid="modal-probability">{current.initial_probability}%</div>
              </div>
            )}
          </div>

          <div className={`${toneClasses.bg} border ${toneClasses.ring} p-3`}>
            <div className={`${toneClasses.text} font-bold text-sm leading-snug`} data-testid="modal-action">
              {meta.action}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2 text-[11px] font-mono">
            {grid.map((g) => (
              <div key={g.l} className="border border-zinc-800 bg-[#121212] p-2">
                <div className="text-zinc-500 uppercase tracking-widest text-[9px]">{g.l}</div>
                <div className={`${g.cls} font-bold`}>
                  {g.v != null ? Number(g.v).toLocaleString("en-IN", { maximumFractionDigits: 5 }) : "—"}
                </div>
              </div>
            ))}
          </div>

          {isNew ? (
            <div className="flex items-center justify-between text-[10px] font-mono uppercase tracking-widest" data-testid="modal-signal-time">
              <span className="text-zinc-500">
                Signal: {new Date(current.generated_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
              </span>
              {signalAge != null && (
                <span className={`font-bold ${ageTone}`}>
                  {signalAge <= 1 ? "JUST NOW" : `${signalAge} MIN AGO`}
                </span>
              )}
            </div>
          ) : (
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
              Closed: {current.closed_at ? new Date(current.closed_at).toLocaleString("en-IN", {
                dateStyle: "medium", timeStyle: "short",
              }) : "—"}
            </div>
          )}

          <button
            onClick={dismiss}
            data-testid="modal-acknowledge"
            className={`w-full ${toneClasses.pill} hover:opacity-90 text-black font-bold uppercase tracking-widest text-xs py-3 transition-opacity`}
          >
            Acknowledged · Got it
          </button>
          {queue.length > 0 && (
            <button
              onClick={dismissAll}
              data-testid="modal-dismiss-all"
              className="w-full border border-zinc-700 hover:border-zinc-500 text-zinc-400 hover:text-white font-mono uppercase tracking-widest text-[10px] py-2 transition-colors"
            >
              Dismiss all ({queue.length + 1} alerts)
            </button>
          )}
        </div>

        {/* Subtle close button (still requires explicit ack to dismiss) */}
        <button
          onClick={dismiss}
          className="absolute top-1.5 right-1.5 text-black/70 hover:text-black p-1"
          aria-label="Dismiss"
          data-testid="modal-close"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
